export type CanonicalComponentRole =
  | "button"
  | "input"
  | "select"
  | "checkbox"
  | "radio"
  | "switch"
  | "slider"
  | "tabs"
  | "dialog"
  | "drawer"
  | "tooltip"
  | "popover"
  | "menu"
  | "navigation"
  | "breadcrumb"
  | "pagination"
  | "table"
  | "card"
  | "badge"
  | "avatar"
  | "alert"
  | "progress"
  | "skeleton";

export type ComponentRoleResolution =
  | { role: CanonicalComponentRole; status: "exact" }
  | { role: null; status: "unmapped" | "ambiguous" };

export const componentRoleAliases: Readonly<
  Record<CanonicalComponentRole, readonly string[]>
> = Object.freeze({
  button: ["button", "buttons"],
  input: ["input", "text input", "text field"],
  select: ["select", "dropdown select"],
  checkbox: ["checkbox", "check box"],
  radio: ["radio", "radio button"],
  switch: ["switch", "toggle switch"],
  slider: ["slider"],
  tabs: ["tabs", "tab group"],
  dialog: ["dialog", "modal"],
  drawer: ["drawer", "side sheet"],
  tooltip: ["tooltip"],
  popover: ["popover"],
  menu: ["menu", "dropdown menu"],
  navigation: ["navigation", "navbar", "navigation bar"],
  breadcrumb: ["breadcrumb", "breadcrumbs"],
  pagination: ["pagination"],
  table: ["table", "data table"],
  card: ["card", "product card"],
  badge: ["badge", "tag"],
  avatar: ["avatar"],
  alert: ["alert", "banner"],
  progress: ["progress", "progress bar"],
  skeleton: ["skeleton", "loading skeleton"],
});

export const ambiguousComponentRoleAliases = Object.freeze([
  "notification",
  "dropdown",
] as const);

export interface ComponentRoleRegistry {
  mapRole(sourceName: string): ComponentRoleResolution;
}

function normalizeAlias(value: string): string {
  return value
    .normalize("NFKC")
    .trim()
    .toLowerCase()
    .replace(/[-_/]+/g, " ")
    .replace(/\s+/g, " ");
}

export function createComponentRoleRegistry(
  aliases: Readonly<Record<CanonicalComponentRole, readonly string[]>>,
  ambiguousAliases: readonly string[],
): ComponentRoleRegistry {
  const exact = new Map<string, CanonicalComponentRole>();
  for (const [role, roleAliases] of Object.entries(aliases) as Array<
    [CanonicalComponentRole, readonly string[]]
  >) {
    for (const alias of roleAliases) {
      const normalized = normalizeAlias(alias);
      const owner = exact.get(normalized);
      if (!normalized || owner !== undefined) {
        throw new Error("COMPONENT_ROLE_REGISTRY_ALIAS_COLLISION");
      }
      exact.set(normalized, role);
    }
  }
  const ambiguous = new Set<string>();
  for (const alias of ambiguousAliases) {
    const normalized = normalizeAlias(alias);
    if (!normalized || exact.has(normalized) || ambiguous.has(normalized)) {
      throw new Error("COMPONENT_ROLE_REGISTRY_ALIAS_COLLISION");
    }
    ambiguous.add(normalized);
  }
  return Object.freeze({
    mapRole(sourceName: string): ComponentRoleResolution {
      const normalized = normalizeAlias(sourceName);
      const role = exact.get(normalized);
      if (role !== undefined) return { role, status: "exact" };
      if (ambiguous.has(normalized)) {
        return { role: null, status: "ambiguous" };
      }
      return { role: null, status: "unmapped" };
    },
  });
}

const registry = createComponentRoleRegistry(
  componentRoleAliases,
  ambiguousComponentRoleAliases,
);

export function mapComponentRole(sourceName: string): ComponentRoleResolution {
  return registry.mapRole(sourceName);
}
